import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../components/ui/Modal';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { fetchRegistryIntel } from '../lib/accountIntel';
import { getAcraEntitiesSyncState, type AcraEntitiesSyncState } from '../lib/acraEntitiesDb';
import { ACRA_CORPORATE_COLLECTION_ID } from '../lib/acraBucketMap';
import {
  acraBizfileSearchUrl,
  buildAcraSsicOptions,
  dataGovSgDatasetUrl,
  fetchAcraEntityTotalCount,
  fetchDataGovSgDatasetMetadata,
  fetchDataGovSgDatastorePreview,
  formatAcraAddress,
  formatAcraSsic,
  formatAcraSsicOption,
  formatAcraSsicShort,
  formatDataGovSgBytes,
  isAcraCorporateDataset,
  loadAcraSsicCatalog,
  loadDataGovSgIndex,
  matchesDataGovSgSearch,
  readDataGovSgIndexCache,
  searchAcraEntities,
  sortDataGovSgDatasets,
  type AcraEntityRecord,
  type AcraSsicOption,
  type DataGovSgDatasetMetadata,
  type DataGovSgDatasetSummary,
  type DataGovSgDatastorePreview,
} from '../lib/dataGovSg';
import type { AccountIntelRegistry } from '../types/crm';
import { formatSingaporeDateTime } from '../utils/dateTime';

const PAGE_SIZE = 25;
const SORT_OPTIONS = ['Recently updated', 'Name A–Z', 'Agency'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];
type PageTab = 'acra' | 'catalog';

function sortKey(option: SortOption): 'updated' | 'name' | 'agency' {
  if (option === 'Name A–Z') return 'name';
  if (option === 'Agency') return 'agency';
  return 'updated';
}

function formatDateTime(value?: string): string {
  if (!value?.trim()) return '—';
  return formatSingaporeDateTime(value);
}

function isAcraActiveStatus(status: string): boolean {
  const value = status.toLowerCase();
  if (value.includes('deregister') || value.includes('struck') || value.includes('cancel')) {
    return false;
  }
  return value.includes('live') || value.includes('register');
}

function displayValue(value?: string): string {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.toLowerCase() === 'na') return '—';
  return trimmed;
}

function mergeSsicOptions(
  existing: AcraSsicOption[],
  incoming: AcraSsicOption[],
): AcraSsicOption[] {
  const map = new Map(existing.map((row) => [row.code, row.description]));
  for (const row of incoming) {
    if (!map.has(row.code)) map.set(row.code, row.description);
  }
  return [...map.entries()]
    .map(([code, description]) => ({ code, description }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

function previewColumns(
  preview: DataGovSgDatastorePreview | null,
  metadata: DataGovSgDatasetMetadata | null,
): string[] {
  if (!preview) return [];
  const map = metadata?.columnMetadata?.map ?? {};
  const fromMap = Object.values(map).filter(Boolean);
  if (fromMap.length) return fromMap.slice(0, 8);
  const first = preview.records[0];
  if (first) return Object.keys(first).slice(0, 8);
  return preview.fields.map((field) => field.id).slice(0, 8);
}

function previewValue(record: Record<string, unknown>, column: string): string {
  const direct = record[column];
  if (direct != null && direct !== '') return String(direct);
  const match = Object.entries(record).find(([key]) => key.toLowerCase() === column.toLowerCase());
  if (match?.[1] != null && match[1] !== '') return String(match[1]);
  return '—';
}

export function DataGovSgPage() {
  const [tab, setTab] = useState<PageTab>('acra');

  const [datasets, setDatasets] = useState<DataGovSgDatasetSummary[]>([]);
  const [indexReady, setIndexReady] = useState(false);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexProgress, setIndexProgress] = useState({ loaded: 0, total: 0 });
  const [indexFetchedAt, setIndexFetchedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [acraSearch, setAcraSearch] = useState('');
  const [acraQuery, setAcraQuery] = useState('');
  const [acraRows, setAcraRows] = useState<AcraEntityRecord[]>([]);
  const [acraTotal, setAcraTotal] = useState(0);
  const [acraPage, setAcraPage] = useState(1);
  const [acraLoading, setAcraLoading] = useState(false);
  const [acraError, setAcraError] = useState<string | null>(null);
  const [acraCatalogTotal, setAcraCatalogTotal] = useState<number | null>(null);
  const [acraSyncState, setAcraSyncState] = useState<AcraEntitiesSyncState | null>(null);
  const [selectedAcra, setSelectedAcra] = useState<AcraEntityRecord | null>(null);
  const [acraRegistry, setAcraRegistry] = useState<AccountIntelRegistry | null>(null);
  const [acraRegistryLoading, setAcraRegistryLoading] = useState(false);
  const [acraRegistryError, setAcraRegistryError] = useState<string | null>(null);
  const [primarySsicFilter, setPrimarySsicFilter] = useState('all');
  const [secondarySsicFilter, setSecondarySsicFilter] = useState('all');
  const [primarySsicOptions, setPrimarySsicOptions] = useState<AcraSsicOption[]>([]);
  const [secondarySsicOptions, setSecondarySsicOptions] = useState<AcraSsicOption[]>([]);

  const [catalogSearch, setCatalogSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('Recently updated');
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [catalogPage, setCatalogPage] = useState(1);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DataGovSgDatasetMetadata | null>(null);
  const [preview, setPreview] = useState<DataGovSgDatastorePreview | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const syncIndex = useCallback(async (force = false) => {
    setIndexLoading(true);
    setError(null);
    try {
      const cache = await loadDataGovSgIndex({
        force,
        onProgress: (loaded, total, rows) => {
          setIndexProgress({ loaded, total });
          setDatasets(rows);
        },
      });
      setDatasets(cache.datasets);
      setIndexFetchedAt(cache.fetchedAt);
      setIndexReady(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync data.gov.sg datasets.');
    } finally {
      setIndexLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void getAcraEntitiesSyncState()
        .then((state) => setAcraSyncState(state))
        .catch(() => setAcraSyncState(null));
      void fetchAcraEntityTotalCount({ background: true })
        .then((total) => setAcraCatalogTotal(total))
        .catch(() => setAcraCatalogTotal(null));
    }, 2500);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (tab !== 'acra') return;
    void loadAcraSsicCatalog()
      .then((catalog) => {
        setPrimarySsicOptions(catalog.primary);
        setSecondarySsicOptions(catalog.secondary);
      })
      .catch(() => {
        // catalog is optional — dropdowns still fill from search results
      });
  }, [tab]);

  useEffect(() => {
    if (tab !== 'catalog' || indexReady || indexLoading) return;
    const cached = readDataGovSgIndexCache();
    if (cached) {
      setDatasets(cached.datasets);
      setIndexFetchedAt(cached.fetchedAt);
      setIndexReady(true);
      setIndexProgress({ loaded: cached.totalPages, total: cached.totalPages });
      return;
    }
    void syncIndex(false);
  }, [tab, indexReady, indexLoading, syncIndex]);

  useEffect(() => {
    const timer = window.setTimeout(() => setAcraQuery(acraSearch.trim()), 600);
    return () => window.clearTimeout(timer);
  }, [acraSearch]);

  useEffect(() => {
    setAcraPage(1);
  }, [acraQuery, primarySsicFilter, secondarySsicFilter]);

  const acraHasSsicFilter = primarySsicFilter !== 'all' || secondarySsicFilter !== 'all';
  const acraSearchActive =
    (acraQuery && acraQuery.length >= 2) || acraHasSsicFilter;

  useEffect(() => {
    if (!acraSearchActive) {
      setAcraRows([]);
      setAcraTotal(0);
      setAcraError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setAcraLoading(true);
    setAcraError(null);

    void searchAcraEntities({
      query: acraQuery,
      offset: (acraPage - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      primarySsicCode: primarySsicFilter !== 'all' ? primarySsicFilter : undefined,
      secondarySsicCode: secondarySsicFilter !== 'all' ? secondarySsicFilter : undefined,
      signal: controller.signal,
    })
      .then((result) => {
        if (cancelled) return;
        setAcraRows(result.records);
        setAcraTotal(result.total);
        setPrimarySsicOptions((current) =>
          mergeSsicOptions(current, buildAcraSsicOptions(result.records, 'primary')),
        );
        setSecondarySsicOptions((current) =>
          mergeSsicOptions(current, buildAcraSsicOptions(result.records, 'secondary')),
        );
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof TypeError && err.message === 'Failed to fetch' && controller.signal.aborted) {
          return;
        }
        setAcraRows([]);
        setAcraTotal(0);
        setAcraError(err instanceof Error ? err.message : 'ACRA search failed.');
      })
      .finally(() => {
        if (!cancelled) setAcraLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [acraQuery, acraPage, acraSearchActive, primarySsicFilter, secondarySsicFilter]);

  useEffect(() => {
    setCatalogPage(1);
  }, [catalogSearch, sort, agencyFilter, formatFilter]);

  const catalogDatasets = useMemo(
    () => datasets.filter((row) => !isAcraCorporateDataset(row)),
    [datasets],
  );

  const agencies = useMemo(() => {
    const values = new Set<string>();
    for (const row of catalogDatasets) {
      if (row.managedByAgencyName && row.managedByAgencyName !== '—') {
        values.add(row.managedByAgencyName);
      }
    }
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [catalogDatasets]);

  const formats = useMemo(() => {
    const values = new Set<string>();
    for (const row of catalogDatasets) {
      if (row.format && row.format !== '—') values.add(row.format);
    }
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [catalogDatasets]);

  const filteredCatalog = useMemo(() => {
    let list = catalogDatasets.filter((row) => matchesDataGovSgSearch(row, catalogSearch));
    if (agencyFilter !== 'all') {
      list = list.filter((row) => row.managedByAgencyName === agencyFilter);
    }
    if (formatFilter !== 'all') {
      list = list.filter((row) => row.format === formatFilter);
    }
    return sortDataGovSgDatasets(list, sortKey(sort));
  }, [catalogDatasets, catalogSearch, agencyFilter, formatFilter, sort]);

  const catalogTotalPages = Math.max(1, Math.ceil(filteredCatalog.length / PAGE_SIZE));
  const safeCatalogPage = Math.min(catalogPage, catalogTotalPages);
  const catalogRows = useMemo(() => {
    const start = (safeCatalogPage - 1) * PAGE_SIZE;
    return filteredCatalog.slice(start, start + PAGE_SIZE);
  }, [filteredCatalog, safeCatalogPage]);

  const catalogFrom =
    filteredCatalog.length === 0 ? 0 : (safeCatalogPage - 1) * PAGE_SIZE + 1;
  const catalogTo = Math.min(safeCatalogPage * PAGE_SIZE, filteredCatalog.length);

  const acraTotalPages = Math.max(1, Math.ceil(acraTotal / PAGE_SIZE));
  const acraFrom = acraTotal === 0 ? 0 : (acraPage - 1) * PAGE_SIZE + 1;
  const acraTo = Math.min(acraPage * PAGE_SIZE, acraTotal);

  const openAcraDetail = useCallback((row: AcraEntityRecord) => {
    setSelectedAcra(row);
    setAcraRegistry(null);
    setAcraRegistryError(null);
    setAcraRegistryLoading(true);
    void fetchRegistryIntel(row.entityName, row.uen)
      .then(({ registry, error }) => {
        setAcraRegistry(registry);
        setAcraRegistryError(error);
      })
      .catch((err) => {
        setAcraRegistry(null);
        setAcraRegistryError(err instanceof Error ? err.message : 'Registry lookup failed.');
      })
      .finally(() => setAcraRegistryLoading(false));
  }, []);

  const closeAcraDetail = () => {
    setSelectedAcra(null);
    setAcraRegistry(null);
    setAcraRegistryError(null);
  };

  const openDetail = useCallback(async (datasetId: string) => {
    setSelectedId(datasetId);
    setDetail(null);
    setPreview(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const [metadata, previewRows] = await Promise.all([
        fetchDataGovSgDatasetMetadata(datasetId),
        fetchDataGovSgDatastorePreview(datasetId, 10).catch(() => null),
      ]);
      setDetail(metadata);
      setPreview(previewRows);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to load dataset details.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setPreview(null);
    setDetailError(null);
  };

  const selectedSummary = selectedId
    ? datasets.find((row) => row.datasetId === selectedId) ?? null
    : null;
  const detailTitle = detail?.name || selectedSummary?.name || 'Dataset details';
  const previewCols = previewColumns(preview, detail);

  return (
    <div className="data-gov-sg-page">
      <PageHeader
        title="data.gov.sg"
        description="Search Singapore-registered corporate entities and browse government open datasets via official data.gov.sg APIs."
        actions={
          <a
            href="https://data.gov.sg/"
            target="_blank"
            rel="noreferrer"
            className="btn-secondary btn-sm"
          >
            Open data.gov.sg
          </a>
        }
      />

      <div className="data-gov-sg-page__tabs" role="tablist" aria-label="data.gov.sg views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'acra'}
          className={`data-gov-sg-page__tab${tab === 'acra' ? ' data-gov-sg-page__tab--active' : ''}`}
          onClick={() => setTab('acra')}
        >
          ACRA entities
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'catalog'}
          className={`data-gov-sg-page__tab${tab === 'catalog' ? ' data-gov-sg-page__tab--active' : ''}`}
          onClick={() => setTab('catalog')}
        >
          Other datasets
        </button>
      </div>

      {tab === 'acra' ? (
        <>
          <div className="data-gov-sg-page__meta card">
            <div className="data-gov-sg-page__meta-copy">
              <strong>
                {(acraCatalogTotal ?? acraSyncState?.entityCount ?? null) != null
                  ? (acraCatalogTotal ?? acraSyncState?.entityCount ?? 0).toLocaleString()
                  : '2M+'}{' '}
                registered entities
              </strong>
              <span>
                Source: ACRA Information on Corporate Entities (Collection 2) · Accounting and
                Corporate Regulatory Authority
                {acraSyncState?.syncCompletedAt
                  ? ` · Local copy updated ${formatDateTime(acraSyncState.syncCompletedAt)}`
                  : acraSyncState?.status === 'running'
                    ? ' · Sync in progress…'
                    : ' · Local copy not synced yet'}
              </span>
            </div>
            <a
              href={`https://data.gov.sg/collections/${ACRA_CORPORATE_COLLECTION_ID}/view`}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary btn-sm"
            >
              View collection
            </a>
          </div>

          <div className="page-toolbar data-gov-sg-page__acra-toolbar">
            <div className="page-toolbar__search-wrap data-gov-sg-page__acra-search">
              <input
                className="field-input page-toolbar__search"
                type="text"
                inputMode="search"
                autoComplete="off"
                placeholder="Company name or UEN"
                value={acraSearch}
                onChange={(e) => setAcraSearch(e.target.value)}
              />
            </div>
            <label className="data-gov-sg-page__acra-filter-inline">
              <span>Primary SSIC</span>
              <select
                className="field-input data-gov-sg-page__filter data-gov-sg-page__filter--ssic"
                value={primarySsicFilter}
                onChange={(e) => setPrimarySsicFilter(e.target.value)}
              >
                <option value="all">All industries</option>
                {primarySsicOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {formatAcraSsicOption(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="data-gov-sg-page__acra-filter-inline">
              <span>Secondary SSIC</span>
              <select
                className="field-input data-gov-sg-page__filter data-gov-sg-page__filter--ssic"
                value={secondarySsicFilter}
                onChange={(e) => setSecondarySsicFilter(e.target.value)}
              >
                <option value="all">All industries</option>
                {secondarySsicOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {formatAcraSsicOption(option)}
                  </option>
                ))}
              </select>
            </label>
            <div className="data-gov-sg-page__toolbar-aside">
              <span className="page-count">
                {acraLoading
                  ? 'Searching…'
                  : acraSearchActive
                    ? `${acraTotal.toLocaleString()} match${acraTotal === 1 ? '' : 'es'}`
                    : 'Search or filter'}
              </span>
              {acraHasSsicFilter ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setPrimarySsicFilter('all');
                    setSecondarySsicFilter('all');
                  }}
                >
                  Clear SSIC
                </button>
              ) : null}
            </div>
          </div>

          {acraError ? <div className="alert alert-error">{acraError}</div> : null}

          {!acraSearchActive ? (
            <div className="empty-state card">
              <p>
                Enter a <strong>company name</strong> or <strong>UEN</strong>, or choose a Primary /
                Secondary SSIC industry to begin.
              </p>
            </div>
          ) : acraLoading && acraRows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__spinner" />
              <p>Searching ACRA entities…</p>
            </div>
          ) : acraRows.length === 0 ? (
            <div className="empty-state card">
              <p>No ACRA entities match your search.</p>
            </div>
          ) : (
            <div className="data-gov-sg-acra-list card">
              <div className="data-gov-sg-acra-list__head" role="row">
                <span>UEN</span>
                <span>Entity name</span>
                <span>Type</span>
                <span>Status</span>
                <span>Primary SSIC</span>
                <span>Reg. date</span>
                <span>Registered address</span>
              </div>
              {acraRows.map((row) => {
                const ssic = formatAcraSsicShort(row);
                return (
                <button
                  key={`${row.uen}-${row.entityName}`}
                  type="button"
                  className="data-gov-sg-acra-list__row"
                  onClick={() => openAcraDetail(row)}
                >
                  <span className="data-gov-sg-acra-list__uen">{row.uen || '—'}</span>
                  <span className="data-gov-sg-acra-list__name">
                    <strong>{row.entityName || '—'}</strong>
                    {row.issuanceAgency ? (
                      <span className="data-gov-sg-acra-list__agency">{row.issuanceAgency}</span>
                    ) : null}
                  </span>
                  <span className="data-gov-sg-acra-list__text">{row.entityType || '—'}</span>
                  <span
                    className={`data-gov-sg-acra-list__status data-gov-sg-acra-list__status--${isAcraActiveStatus(row.status) ? 'active' : 'inactive'}`}
                  >
                    {row.status || '—'}
                  </span>
                  <span className="data-gov-sg-acra-list__ssic">
                    {ssic.code ? (
                      <span className="data-gov-sg-acra-list__ssic-code">{ssic.code}</span>
                    ) : (
                      <span className="data-gov-sg-acra-list__ssic-code">—</span>
                    )}
                    {ssic.description ? (
                      <span className="data-gov-sg-acra-list__ssic-desc">{ssic.description}</span>
                    ) : null}
                  </span>
                  <span className="data-gov-sg-acra-list__text">
                    {row.registrationDate || row.issueDate || '—'}
                  </span>
                  <span className="data-gov-sg-acra-list__text data-gov-sg-acra-list__address">
                    {formatAcraAddress(row)}
                  </span>
                </button>
              );
              })}
            </div>
          )}

          {acraSearchActive && acraTotal > 0 ? (
            <div className="data-gov-sg-page__pagination">
              <span>
                Showing {acraFrom.toLocaleString()}–{acraTo.toLocaleString()} of{' '}
                {acraTotal.toLocaleString()}
              </span>
              <div className="data-gov-sg-page__pagination-actions">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={acraPage <= 1 || acraLoading}
                  onClick={() => setAcraPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span>
                  Page {acraPage} / {acraTotalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={acraPage >= acraTotalPages || acraLoading}
                  onClick={() => setAcraPage((current) => Math.min(acraTotalPages, current + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}

          <Modal
            title={selectedAcra?.entityName || 'ACRA entity'}
            open={Boolean(selectedAcra)}
            onClose={closeAcraDetail}
            wide
            headerActions={
              selectedAcra?.uen ? (
                <a
                  href={acraBizfileSearchUrl(selectedAcra.uen)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary btn-sm"
                >
                  Open on Bizfile
                </a>
              ) : null
            }
          >
            {selectedAcra ? (
              <div className="data-gov-sg-acra-detail">
                <section className="data-gov-sg-acra-detail__section">
                  <h3>Registration</h3>
                  <dl className="data-gov-sg-acra-detail__grid">
                    <div>
                      <dt>UEN</dt>
                      <dd>{selectedAcra.uen || '—'}</dd>
                    </div>
                    <div>
                      <dt>Legal name</dt>
                      <dd>{acraRegistry?.legalName || selectedAcra.entityName || '—'}</dd>
                    </div>
                    <div>
                      <dt>Entity type</dt>
                      <dd>{displayValue(selectedAcra.entityType)}</dd>
                    </div>
                    <div>
                      <dt>Company type</dt>
                      <dd>{displayValue(selectedAcra.companyType)}</dd>
                    </div>
                    <div>
                      <dt>Business constitution</dt>
                      <dd>{displayValue(selectedAcra.businessConstitution)}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{displayValue(selectedAcra.status)}</dd>
                    </div>
                    <div>
                      <dt>Registration date</dt>
                      <dd>{displayValue(selectedAcra.registrationDate || selectedAcra.issueDate)}</dd>
                    </div>
                    <div>
                      <dt>UEN issue date</dt>
                      <dd>{displayValue(selectedAcra.issueDate)}</dd>
                    </div>
                    <div>
                      <dt>Issuing agency</dt>
                      <dd>{displayValue(selectedAcra.issuanceAgency)}</dd>
                    </div>
                    <div>
                      <dt>Officers</dt>
                      <dd>{displayValue(selectedAcra.noOfOfficers)}</dd>
                    </div>
                  </dl>
                </section>

                <section className="data-gov-sg-acra-detail__section">
                  <h3>Registered address</h3>
                  <p className="data-gov-sg-acra-detail__address">{formatAcraAddress(selectedAcra)}</p>
                </section>

                <section className="data-gov-sg-acra-detail__section">
                  <h3>Industry (SSIC)</h3>
                  <dl className="data-gov-sg-acra-detail__grid">
                    <div className="data-gov-sg-acra-detail__wide">
                      <dt>Primary SSIC</dt>
                      <dd>{formatAcraSsic(selectedAcra)}</dd>
                    </div>
                    <div className="data-gov-sg-acra-detail__wide">
                      <dt>Primary activity</dt>
                      <dd>{displayValue(selectedAcra.primaryActivity)}</dd>
                    </div>
                    <div className="data-gov-sg-acra-detail__wide">
                      <dt>Secondary SSIC</dt>
                      <dd>
                        {selectedAcra.secondarySsicCode || selectedAcra.secondarySsicDescription
                          ? `${displayValue(selectedAcra.secondarySsicCode)} — ${displayValue(selectedAcra.secondarySsicDescription)}`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className="data-gov-sg-acra-detail__section">
                  <h3>Filing & capital</h3>
                  <dl className="data-gov-sg-acra-detail__grid">
                    <div>
                      <dt>Account due date</dt>
                      <dd>{displayValue(selectedAcra.accountDueDate)}</dd>
                    </div>
                    <div>
                      <dt>Annual return date</dt>
                      <dd>{displayValue(selectedAcra.annualReturnDate)}</dd>
                    </div>
                    <div>
                      <dt>Paid-up capital</dt>
                      <dd>
                        {acraRegistryLoading && !acraRegistry?.paidUpCapital
                          ? 'Loading…'
                          : acraRegistry?.paidUpCapital
                            ? `${Number(acraRegistry.paidUpCapital).toLocaleString()}${acraRegistry.currency ? ` ${acraRegistry.currency}` : ''}`
                            : '—'}
                      </dd>
                    </div>
                  </dl>
                </section>

                {selectedAcra.formerNames.length > 0 ? (
                  <section className="data-gov-sg-acra-detail__section">
                    <h3>Former names</h3>
                    <ul className="data-gov-sg-acra-detail__list">
                      {selectedAcra.formerNames.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {selectedAcra.auditFirms.length > 0 ? (
                  <section className="data-gov-sg-acra-detail__section">
                    <h3>Audit firms</h3>
                    <ul className="data-gov-sg-acra-detail__list">
                      {selectedAcra.auditFirms.map((firm) => (
                        <li key={`${firm.uen}-${firm.name}`}>
                          {firm.name || '—'}
                          {firm.uen ? ` (${firm.uen})` : ''}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {acraRegistryError ? <div className="alert alert-error">{acraRegistryError}</div> : null}

                {acraRegistry?.source ? (
                  <p className="data-gov-sg-acra-detail__footnote">Source: {acraRegistry.source}</p>
                ) : null}
              </div>
            ) : null}
          </Modal>
        </>
      ) : (
        <>
          <div className="kpi-grid dashboard-kpis data-gov-sg-page__kpis">
            <div className="card kpi-card">
              <div className="kpi-label">Other datasets</div>
              <div className="kpi-value">
                {indexReady ? catalogDatasets.length.toLocaleString() : '…'}
              </div>
            </div>
            <div className="card kpi-card">
              <div className="kpi-label">Agencies</div>
              <div className="kpi-value">{indexReady ? agencies.length.toLocaleString() : '…'}</div>
            </div>
            <div className="card kpi-card">
              <div className="kpi-label">Index status</div>
              <div className="kpi-value kpi-value--compact">
                {indexLoading ? 'Syncing…' : indexReady ? 'Ready' : 'Not loaded'}
              </div>
              {indexFetchedAt ? (
                <p className="kpi-card__hint">Updated {formatDateTime(indexFetchedAt)}</p>
              ) : null}
            </div>
          </div>

          {indexLoading && indexProgress.total > 0 ? (
            <div className="card data-gov-sg-page__progress">
              <div className="data-gov-sg-page__progress-copy">
                <strong>Syncing dataset index</strong>
                <span>
                  Page {indexProgress.loaded} / {indexProgress.total} ·{' '}
                  {datasets.length.toLocaleString()} datasets loaded
                </span>
              </div>
              <div className="data-gov-sg-page__progress-bar" aria-hidden>
                <span
                  style={{
                    width: `${Math.min(100, (indexProgress.loaded / Math.max(indexProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ) : null}

          {error ? <div className="alert alert-error">{error}</div> : null}

          <PageToolbar
            search={catalogSearch}
            onSearchChange={setCatalogSearch}
            searchPlaceholder="Search dataset name, agency, format or ID"
            sortValue={sort}
            sortOptions={[...SORT_OPTIONS]}
            onSortChange={(value) => setSort(value as SortOption)}
            action={
              <div className="data-gov-sg-page__toolbar-aside">
                <select
                  className="field-input data-gov-sg-page__filter"
                  value={agencyFilter}
                  onChange={(e) => setAgencyFilter(e.target.value)}
                >
                  <option value="all">All agencies</option>
                  {agencies.map((agency) => (
                    <option key={agency} value={agency}>
                      {agency}
                    </option>
                  ))}
                </select>
                <select
                  className="field-input data-gov-sg-page__filter"
                  value={formatFilter}
                  onChange={(e) => setFormatFilter(e.target.value)}
                >
                  <option value="all">All formats</option>
                  {formats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
                <span className="page-count">
                  {indexLoading && !indexReady
                    ? 'Loading…'
                    : `${filteredCatalog.length.toLocaleString()} dataset${filteredCatalog.length === 1 ? '' : 's'}`}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={indexLoading}
                  onClick={() => void syncIndex(true)}
                >
                  Refresh index
                </button>
              </div>
            }
          />

          <p className="data-gov-sg-page__catalog-note">
            ACRA corporate entity rows are shown in the <strong>ACRA entities</strong> tab. This
            catalog only lists other government datasets (weather, transport, health, etc.).
          </p>

          {indexLoading && catalogDatasets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__spinner" />
              <p>Loading datasets from data.gov.sg…</p>
            </div>
          ) : filteredCatalog.length === 0 ? (
            <div className="empty-state card">
              <p>No datasets match your filters.</p>
            </div>
          ) : (
            <div className="data-gov-sg-list card">
              <div className="data-gov-sg-list__head" role="row">
                <span>Dataset</span>
                <span>Agency</span>
                <span>Format</span>
                <span>Last updated</span>
                <span>Status</span>
              </div>
              {catalogRows.map((row) => (
                <button
                  key={row.datasetId}
                  type="button"
                  className="data-gov-sg-list__row"
                  onClick={() => void openDetail(row.datasetId)}
                >
                  <span className="data-gov-sg-list__name">
                    <strong>{row.name}</strong>
                    {row.description ? (
                      <span className="data-gov-sg-list__description">{row.description}</span>
                    ) : null}
                  </span>
                  <span className="data-gov-sg-list__text">{row.managedByAgencyName}</span>
                  <span className="data-gov-sg-list__badge">{row.format}</span>
                  <span className="data-gov-sg-list__text">{formatDateTime(row.lastUpdatedAt)}</span>
                  <span className={`data-gov-sg-list__status data-gov-sg-list__status--${row.status}`}>
                    {row.status}
                  </span>
                </button>
              ))}
            </div>
          )}

          {filteredCatalog.length > 0 ? (
            <div className="data-gov-sg-page__pagination">
              <span>
                Showing {catalogFrom.toLocaleString()}–{catalogTo.toLocaleString()} of{' '}
                {filteredCatalog.length.toLocaleString()}
              </span>
              <div className="data-gov-sg-page__pagination-actions">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={safeCatalogPage <= 1}
                  onClick={() => setCatalogPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span>
                  Page {safeCatalogPage} / {catalogTotalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={safeCatalogPage >= catalogTotalPages}
                  onClick={() => setCatalogPage((current) => Math.min(catalogTotalPages, current + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}

      <Modal
        title={detailTitle}
        open={Boolean(selectedId)}
        onClose={closeDetail}
        wide
        headerActions={
          selectedId ? (
            <a
              href={dataGovSgDatasetUrl(selectedId)}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary btn-sm"
            >
              Open on data.gov.sg
            </a>
          ) : null
        }
      >
        {detailLoading ? (
          <div className="empty-state">
            <div className="empty-state__spinner" />
            <p>Loading dataset details…</p>
          </div>
        ) : detailError ? (
          <div className="alert alert-error">{detailError}</div>
        ) : detail ? (
          <div className="data-gov-sg-detail">
            <div className="data-gov-sg-detail__meta">
              <div>
                <span className="data-gov-sg-detail__label">Agency</span>
                <strong>{detail.managedBy || detail.managedByAgencyName}</strong>
              </div>
              <div>
                <span className="data-gov-sg-detail__label">Format</span>
                <strong>{detail.format}</strong>
              </div>
              <div>
                <span className="data-gov-sg-detail__label">Last updated</span>
                <strong>{formatDateTime(detail.lastUpdatedAt)}</strong>
              </div>
              <div>
                <span className="data-gov-sg-detail__label">Created</span>
                <strong>{formatDateTime(detail.createdAt)}</strong>
              </div>
              <div>
                <span className="data-gov-sg-detail__label">Coverage</span>
                <strong>
                  {formatDateTime(detail.coverageStart)} – {formatDateTime(detail.coverageEnd)}
                </strong>
              </div>
              <div>
                <span className="data-gov-sg-detail__label">Dataset size</span>
                <strong>{formatDataGovSgBytes(detail.datasetSize)}</strong>
              </div>
            </div>

            {detail.description ? (
              <div className="data-gov-sg-detail__description">{detail.description}</div>
            ) : null}

            {detail.contactEmails?.length ? (
              <p className="data-gov-sg-detail__contacts">
                Contact: {detail.contactEmails.join(', ')}
              </p>
            ) : null}

            {preview && preview.records.length > 0 ? (
              <div className="data-gov-sg-detail__preview">
                <div className="data-gov-sg-detail__preview-head">
                  <h3>Preview</h3>
                  <span>{preview.total.toLocaleString()} total rows</span>
                </div>
                <div className="data-gov-sg-detail__table-wrap">
                  <table className="data-gov-sg-detail__table">
                    <thead>
                      <tr>
                        {previewCols.map((column) => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.records.map((record, index) => (
                        <tr key={index}>
                          {previewCols.map((column) => (
                            <td key={column}>{previewValue(record, column)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="data-gov-sg-detail__preview-empty">
                Row preview is not available for this dataset format.
              </p>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
